import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  FaBook,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileAlt,
  FaDownload,
  FaSearch,
  FaFolder,
  FaLock,
  FaVideo,
  FaLink,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { firestore } from "../Firebase";
import Bg from "../Images/about.jpg";

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const navigate = useNavigate();

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const categories = [
    { value: "all", label: "All Resources" },
    { value: "past-papers", label: "Past Papers" },
    { value: "notes", label: "Lecture Notes" },
    { value: "tutorials", label: "Tutorials" },
    { value: "projects", label: "Projects" },
    { value: "other", label: "Other" },
  ];

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return <FaFilePdf size={30} color="#ef4444" />;
      case "doc":
      case "docx":
        return <FaFileWord size={30} color="#3b82f6" />;
      case "xls":
      case "xlsx":
        return <FaFileExcel size={30} color="#22c55e" />;
      case "ppt":
      case "pptx":
        return <FaFilePowerpoint size={30} color="#f97316" />;
      case "video":
        return <FaVideo size={30} color="#a855f7" />;
      case "link":
        return <FaLink size={30} color="#00d9ff" />;
      default:
        return <FaFileAlt size={30} color="#00d9ff" />;
    }
  };

  useEffect(() => {
    const checkAccess = async () => {
      const storedMember = localStorage.getItem("member");
      if (!storedMember) {
        navigate("/Client/login");
        return;
      }

      const memberData = JSON.parse(storedMember);

      // Verify member is approved
      try {
        const doc = await firestore.collection("registrations").doc(memberData.id).get();
        if (!doc.exists || doc.data().status !== "approved") {
          navigate("/Client/dashboard");
          return;
        }
        setMember({ id: doc.id, ...doc.data() });
      } catch (error) {
        console.error("Error checking access:", error);
        navigate("/Client/dashboard");
        return;
      }

      // Fetch resources
      try {
        const snapshot = await firestore
          .collection("resources")
          .orderBy("createdAt", "desc")
          .get();
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setResources(data);
      } catch (error) {
        console.error("Error fetching resources:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [navigate]);

  const filteredResources = resources.filter((resource) => {
    const matchesSearch =
      resource.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
          backgroundSize: "cover",
        }}
      >
        <CircularProgress sx={{ color: "#00d9ff" }} size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "15px",
                background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaBook size={30} color="white" />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: 'white' }}>
                Member Resources
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>
                Access exclusive study materials and learning resources
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card sx={{ borderRadius: "20px", ...darkGlassStyle, my: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search resources..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderRadius: "12px",
                        color: "white",
                        "& fieldset": { borderColor: "rgba(0, 217, 255, 0.3)" },
                        "&:hover fieldset": { borderColor: "rgba(0, 217, 255, 0.5)" },
                        "&.Mui-focused fieldset": { borderColor: "#00d9ff" },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaSearch color="#00d9ff" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {categories.map((cat) => (
                      <Chip
                        key={cat.value}
                        label={cat.label}
                        onClick={() => setSelectedCategory(cat.value)}
                        sx={{
                          background:
                            selectedCategory === cat.value
                              ? "linear-gradient(135deg, #00d9ff, #00b4d8)"
                              : "rgba(0, 217, 255, 0.1)",
                          color: "white",
                          fontWeight: 600,
                          border: `1px solid ${selectedCategory === cat.value ? "#00d9ff" : "rgba(0, 217, 255, 0.3)"}`,
                          "&:hover": {
                            background: "rgba(0, 217, 255, 0.2)",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </motion.div>

        {/* Resources Grid */}
        <Grid container spacing={3}>
          {filteredResources.length > 0 ? (
            filteredResources.map((resource, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={resource.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Card
                    sx={{
                      borderRadius: "20px",
                      ...darkGlassStyle,
                      height: "100%",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        border: "1px solid rgba(0, 217, 255, 0.5)",
                        boxShadow: "0 15px 40px rgba(0, 217, 255, 0.2)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                        <Box
                          sx={{
                            width: 70,
                            height: 70,
                            borderRadius: "15px",
                            background: "rgba(0, 0, 0, 0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {getFileIcon(resource.fileType)}
                        </Box>
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          textAlign: "center",
                          mb: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {resource.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.6)",
                          textAlign: "center",
                          mb: 2,
                          height: 40,
                          overflow: "hidden",
                        }}
                      >
                        {resource.description}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                        <Chip
                          label={categories.find((c) => c.value === resource.category)?.label || "Resource"}
                          size="small"
                          sx={{
                            background: "rgba(0, 217, 255, 0.2)",
                            color: "#00d9ff",
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<FaDownload />}
                        onClick={() => window.open(resource.fileUrl, "_blank")}
                        sx={{
                          borderRadius: "10px",
                          background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                          fontWeight: 600,
                          "&:hover": {
                            background: "linear-gradient(135deg, #00b4d8, #0096c7)",
                          },
                        }}
                      >
                        Download
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Card sx={{ borderRadius: "20px", ...darkGlassStyle }}>
                  <CardContent sx={{ py: 8, textAlign: "center" }}>
                    <FaFolder size={60} color="rgba(255,255,255,0.3)" />
                    <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.5)", mt: 2 }}>
                      No Resources Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.4)", mt: 1 }}>
                      {searchTerm
                        ? "Try adjusting your search or filter"
                        : "Resources will be added by administrators soon"}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          )}
        </Grid>

        {/* Back Button */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/Client/dashboard")}
            sx={{
              borderRadius: "12px",
              px: 4,
              borderColor: "rgba(0, 217, 255, 0.5)",
              color: "#00d9ff",
              "&:hover": {
                borderColor: "#00d9ff",
                background: "rgba(0, 217, 255, 0.1)",
              },
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Resources;
